import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Subject, of } from "rxjs";
import { catchError, debounceTime, switchMap, takeUntil } from "rxjs/operators";
import { NotificationsControllerService } from "../../../core/api/controllers/notifications/notifications-controller.service";
import {
  ENotificationAudience,
  INotificationHistoryItem,
  INotificationRecipient,
  INotificationRecipientsResponse,
  ISendGeneralNotificationRequest,
  NOTIFICATION_BODY_MAX_LENGTH,
  NOTIFICATION_TITLE_MAX_LENGTH,
} from "../../../core/api/controllers/notifications/data/notification.models";
import { NotificationService } from "../../../core/services/notification.service";
import { ConfirmationDialogComponent } from "../../../shared/components/confirmation-dialog/confirmation-dialog.component";
import { ILocation } from "../../../shared/interfaces/ILocation";

@Component({
  selector: "app-push-notifications",
  templateUrl: "./push-notifications.component.html",
  styleUrls: ["./push-notifications.component.scss"],
})
export class PushNotificationsComponent implements OnInit, OnDestroy {
  readonly audiences = ENotificationAudience;
  readonly titleMaxLength = NOTIFICATION_TITLE_MAX_LENGTH;
  readonly bodyMaxLength = NOTIFICATION_BODY_MAX_LENGTH;

  notificationForm: FormGroup;
  selectedLocations: ILocation[] = [];

  /**
   * Kept as whole objects rather than ids so the chips stay readable after the results list has
   * moved on to another search or page.
   */
  selectedUsers: INotificationRecipient[] = [];
  recipients: INotificationRecipient[] = [];
  recipientSearch = "";
  recipientPage = 1;
  recipientPageSize = 25;
  recipientTotalCount = 0;
  isRecipientsLoading = false;

  history: INotificationHistoryItem[] = [];
  historyPage = 1;
  historyPageSize = 10;
  historyTotalCount = 0;

  isSending = false;
  isHistoryLoading = false;

  private readonly recipientQuery$ = new Subject<{ search: string; page: number }>();
  private readonly destroyed$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private notificationsController: NotificationsControllerService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
  ) {
    this.notificationForm = this.formBuilder.group({
      title: [
        "",
        [Validators.required, Validators.maxLength(NOTIFICATION_TITLE_MAX_LENGTH)],
      ],
      body: [
        "",
        [Validators.required, Validators.maxLength(NOTIFICATION_BODY_MAX_LENGTH)],
      ],
      audience: [ENotificationAudience.AllUsers, Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadHistory();
    this.listenForRecipientQueries();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get audience(): ENotificationAudience {
    return this.notificationForm.get("audience").value;
  }

  get isLocationAudience(): boolean {
    return this.audience === ENotificationAudience.SelectedLocations;
  }

  get isUserAudience(): boolean {
    return this.audience === ENotificationAudience.SelectedUsers;
  }

  get selectedLocationIds(): string[] {
    return this.selectedLocations.map((location) => location.id);
  }

  get selectedUserIds(): string[] {
    return this.selectedUsers.map((user) => user.id);
  }

  get totalRecipientPages(): number {
    return Math.max(1, Math.ceil(this.recipientTotalCount / this.recipientPageSize));
  }

  /** Recipients that would get the announcement in the app only, having no device registered. */
  get selectedUsersWithoutDevices(): number {
    return this.selectedUsers.filter((user) => user.deviceCount === 0).length;
  }

  /**
   * Loads the first page lazily, the first time the user audience is picked - most announcements
   * never open the picker, and it is the only audience that costs a request.
   */
  onAudienceChanged(): void {
    if (this.isUserAudience && !this.recipients.length && !this.isRecipientsLoading) {
      this.searchRecipients("");
    }
  }

  displayName(user: INotificationRecipient): string {
    const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return name || user.username;
  }

  isUserSelected(user: INotificationRecipient): boolean {
    return this.selectedUsers.some((selected) => selected.id === user.id);
  }

  toggleUser(user: INotificationRecipient): void {
    if (this.isUserSelected(user)) {
      this.removeUser(user);
      return;
    }

    this.selectedUsers = [...this.selectedUsers, user];
  }

  removeUser(user: INotificationRecipient): void {
    this.selectedUsers = this.selectedUsers.filter(
      (selected) => selected.id !== user.id,
    );
  }

  clearSelectedUsers(): void {
    this.selectedUsers = [];
  }

  searchRecipients(search: string): void {
    this.recipientSearch = search;
    this.recipientPage = 1;
    this.isRecipientsLoading = true;
    this.recipientQuery$.next({ search, page: 1 });
  }

  changeRecipientPage(delta: number): void {
    const nextPage = this.recipientPage + delta;
    if (nextPage < 1 || nextPage > this.totalRecipientPages) {
      return;
    }

    this.recipientPage = nextPage;
    this.isRecipientsLoading = true;
    this.recipientQuery$.next({ search: this.recipientSearch, page: nextPage });
  }

  /**
   * One switchMap'd stream rather than a call per keystroke, so a slow early response cannot land
   * after a later one and show stale results.
   *
   * Deliberately not deduplicated: retyping the same search has to re-issue the request, because
   * every emission is what clears the loading flag. Debounce already collapses the keystrokes.
   */
  private listenForRecipientQueries(): void {
    this.recipientQuery$
      .pipe(
        debounceTime(300),
        switchMap((query) =>
          this.notificationsController
            .getRecipients(query.search, query.page, this.recipientPageSize)
            .pipe(
              catchError((error) => {
                this.notificationService.error(error);
                return of<INotificationRecipientsResponse>({
                  items: [],
                  page: query.page,
                  pageSize: this.recipientPageSize,
                  totalCount: 0,
                });
              }),
            ),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe((response) => {
        this.recipients = response.items;
        this.recipientTotalCount = response.totalCount;
        this.isRecipientsLoading = false;
      });
  }

  get titleLength(): number {
    return (this.notificationForm.get("title").value || "").length;
  }

  get bodyLength(): number {
    return (this.notificationForm.get("body").value || "").length;
  }

  get totalHistoryPages(): number {
    return Math.max(1, Math.ceil(this.historyTotalCount / this.historyPageSize));
  }

  /**
   * The picker toggles location.isSelected before emitting, so this mirrors its selection
   * without taking over its own submit flow.
   */
  onLocationPressed(location: ILocation): void {
    if (location.isSelected) {
      this.selectedLocations = [...this.selectedLocations, location];
    } else {
      this.selectedLocations = this.selectedLocations.filter(
        (selected) => selected.id !== location.id,
      );
    }
  }

  /** Sends to the superadmin's own devices only, so a broadcast can be checked before it goes out. */
  sendTest(): void {
    if (!this.validateCopy()) {
      return;
    }

    this.send(ENotificationAudience.SelfOnly);
  }

  submit(): void {
    if (!this.validateCopy()) {
      return;
    }

    if (this.isLocationAudience && this.selectedLocations.length === 0) {
      this.notificationService.warn("Select at least one location.");
      return;
    }

    if (this.isUserAudience && this.selectedUsers.length === 0) {
      this.notificationService.warn("Select at least one user.");
      return;
    }

    // A broadcast cannot be recalled once devices have it.
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: "Send push notification",
          message: this.buildConfirmationMessage(),
          confirmButtonLabel: "Send notification",
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.send(this.audience);
        }
      });
  }

  loadHistory(page: number = this.historyPage): void {
    this.isHistoryLoading = true;
    this.historyPage = page;

    this.notificationsController
      .getHistory(this.historyPage, this.historyPageSize)
      .subscribe({
        next: (response) => {
          this.history = response.items;
          this.historyTotalCount = response.totalCount;
          this.isHistoryLoading = false;
        },
        error: (error) => {
          this.isHistoryLoading = false;
          this.notificationService.error(error);
        },
      });
  }

  changeHistoryPage(delta: number): void {
    const nextPage = this.historyPage + delta;
    if (nextPage < 1 || nextPage > this.totalHistoryPages) {
      return;
    }

    this.loadHistory(nextPage);
  }

  private validateCopy(): boolean {
    if (this.notificationForm.get("title").invalid || this.notificationForm.get("body").invalid) {
      this.notificationForm.markAllAsTouched();
      return false;
    }

    return true;
  }

  private send(audience: ENotificationAudience): void {
    this.isSending = true;

    // Each list is sent only for the audience that uses it. The backend rejects ids the chosen
    // audience would ignore, so a leftover selection from a since-changed radio must not travel.
    const request: ISendGeneralNotificationRequest = {
      title: this.notificationForm.get("title").value,
      body: this.notificationForm.get("body").value,
      audience,
      locationIds:
        audience === ENotificationAudience.SelectedLocations
          ? this.selectedLocationIds
          : [],
      userIds:
        audience === ENotificationAudience.SelectedUsers ? this.selectedUserIds : [],
    };

    this.notificationsController.sendGeneral(request).subscribe({
      next: (response) => {
        this.isSending = false;

        if (response.deviceCount === 0) {
          this.notificationService.warn(
            "No registered devices matched this audience. Nothing was sent.",
          );
        } else if (response.failureCount > 0) {
          this.notificationService.warn(
            `Delivered to ${response.successCount} of ${response.deviceCount} device(s). ${response.failureCount} failed.`,
          );
        } else {
          this.notificationService.success(
            `Delivered to ${response.successCount} device(s).`,
          );
        }

        this.loadHistory(1);
      },
      error: (error) => {
        this.isSending = false;
        this.notificationService.error(error);
      },
    });
  }

  private buildConfirmationMessage(): string {
    if (this.audience === ENotificationAudience.AllUsers) {
      return "This sends a push notification to every registered device on every location. Continue?";
    }

    if (this.isUserAudience) {
      const userCount = this.selectedUsers.length;
      const withoutDevices = this.selectedUsersWithoutDevices;
      const base = `This sends a push notification to ${userCount} selected user${
        userCount === 1 ? "" : "s"
      }, on every device they are signed in on.`;

      // Worth saying up front, otherwise the delivery count looks wrong afterwards.
      const caveat = withoutDevices
        ? ` ${withoutDevices} of them ${
            withoutDevices === 1 ? "has" : "have"
          } no registered device and will only see it in the app.`
        : "";

      return `${base}${caveat} Continue?`;
    }

    const count = this.selectedLocations.length;
    return `This sends a push notification to every user of ${count} selected location${
      count === 1 ? "" : "s"
    }, on every device they are signed in on. Continue?`;
  }
}
